import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { supplierinvoicesApi } from "../../../api/supplierinvoices";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { toast } from "react-hot-toast";
import {
  ArrowLeftIcon,
  DocumentTextIcon,
  BuildingOffice2Icon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { formatter, formattedDate } from "../../../components/Utils";

export const SupplierInvoiceDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (id) {
      fetchInvoice();
    }
  }, [id]);

  const fetchInvoice = async () => {
    try {
      setIsLoading(true);
      const response = await supplierinvoicesApi.getSupplierInvoice(id, {
        fields: "id,invoice_number,invoice_date,invoice_due_date,amount,amount_excluding_vat,vat,supplier,project,postings",
      });
      if (response.data?.success) {
        setInvoice(response.data.data);
      } else {
        toast.error("Kunne ikke hente faktura");
      }
    } catch (error) {
      console.error("Error fetching supplier invoice:", error);
      toast.error("Kunne ikke hente faktura");
    } finally {
      setIsLoading(false);
    }
  };

  // Group postings by voucher_id
  const groupPostingsByVoucher = (postings) => {
    if (!postings || postings.length === 0) return {};
    
    const grouped = {};
    postings.forEach((posting) => {
      const voucherId = posting.voucher_id || "no-voucher";
      if (!grouped[voucherId]) {
        grouped[voucherId] = [];
      }
      grouped[voucherId].push(posting);
    });
    return grouped;
  };

  // Calculate totals for a group of postings, excluding payment entries
  const calculatePostingTotals = (postings) => {
    if (!postings || postings.length === 0) {
      return { amount: 0, amount_gross: 0, vat: 0 };
    }
    
    // Filter out payment postings - we want expense postings and VAT postings
    const expensePostings = postings.filter((posting) => {
      const amountGross = parseFloat(posting.amount_gross || 0);
      const accountNum = posting.account_number || posting.account_id?.toString() || "";
      
      // Exclude negative amounts (payments)
      if (amountGross < 0) return false;
      
      // Exclude payment accounts (2400 = Leverandørgjeld, 1920 = Bankinnskudd)
      if (accountNum === "2400" || accountNum.startsWith("1920")) return false;
      
      return true;
    });
    
    // Separate expense postings from VAT postings
    const actualExpensePostings = expensePostings.filter((p) => {
      const accountNum = p.account_number || p.account_id?.toString() || "";
      return !accountNum.startsWith("27");
    });
    
    const vatPostings = expensePostings.filter((p) => {
      const accountNum = p.account_number || p.account_id?.toString() || "";
      return accountNum.startsWith("27");
    });
    
    // Calculate totals
    const expenseAmountExclVat = actualExpensePostings.reduce(
      (sum, posting) => sum + parseFloat(posting.amount || 0),
      0
    );
    
    const vatFromExpenses = actualExpensePostings.reduce(
      (sum, posting) => {
        const amount = parseFloat(posting.amount || 0);
        const amountGross = parseFloat(posting.amount_gross || 0);
        return sum + (amountGross - amount);
      },
      0
    );
    
    const deductibleVat = vatPostings.reduce(
      (sum, posting) => sum + parseFloat(posting.amount || 0),
      0
    );
    
    // amount_excluding_vat = expense postings (excl. VAT) - this is the actual expense amount before VAT deduction
    // This represents what was actually spent, not the net cost after VAT deduction
    const amountExclVat = expenseAmountExclVat;
    // Total including VAT (for display) = expense postings (excl. VAT) + VAT on expenses
    const amountGross = expenseAmountExclVat + vatFromExpenses;
    
    return {
      amount: amountExclVat, // Expense amount excluding VAT (before deduction)
      amount_gross: amountGross, // Total expenses including VAT
      vat: vatFromExpenses, // VAT on expenses
      deductible_vat: deductibleVat, // Deductible VAT (for reference)
      net_cost: expenseAmountExclVat - deductibleVat, // Net cost after VAT deduction (for reference)
    };
  };

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <DashboardHeader title="Leverandørfaktura" />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Henter faktura...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="flex flex-col h-full">
        <DashboardHeader title="Leverandørfaktura" />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <DocumentTextIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Faktura ikke funnet</p>
            <button
              onClick={() => navigate("/dashboard/supplier-invoices")}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Tilbake til liste
            </button>
          </div>
        </div>
      </div>
    );
  }

  const groupedPostings = groupPostingsByVoucher(invoice.postings);
  const invoiceTotals = calculatePostingTotals(invoice.postings);

  return (
    <div className="flex flex-col h-full">
      <DashboardHeader
        title={`Leverandørfaktura: ${invoice.invoice_number || `#${invoice.id}`}`}
        actions={[
          {
            component: (
              <button
                onClick={() => navigate("/dashboard/supplier-invoices")}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                <ArrowLeftIcon className="w-5 h-5" />
                Tilbake
              </button>
            ),
          },
        ]}
      />

      <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8 pb-24">
        <div className="mt-8">
        {/* Invoice Header */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {invoice.invoice_number || `Faktura #${invoice.id}`}
              </h2>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <UserIcon className="w-5 h-5 text-gray-400" />
                  <span className="text-gray-600">
                    <strong>Leverandør:</strong> {invoice.supplier?.name || "Ukjent"}
                  </span>
                </div>
                {invoice.supplier?.customer_number && (
                  <div className="text-gray-600 ml-7">
                    <strong>Kundenummer:</strong> {invoice.supplier.customer_number}
                  </div>
                )}
                {invoice.project && (
                  <div className="flex items-center gap-2">
                    <BuildingOffice2Icon className="w-5 h-5 text-gray-400" />
                    <span className="text-gray-600">
                      <strong>Prosjekt:</strong>{" "}
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded">
                        {invoice.project.name}
                      </span>
                    </span>
                  </div>
                )}
                <div className="text-gray-600 ml-7">
                  <strong>Fakturadato:</strong> {formattedDate(invoice.invoice_date)}
                </div>
                {invoice.invoice_due_date && (
                  <div className="text-gray-600 ml-7">
                    <strong>Forfallsdato:</strong> {formattedDate(invoice.invoice_due_date)}
                  </div>
                )}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Summering</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Beløp ekskl. mva:</span>
                  <span className="font-semibold text-gray-900">
                    {formatter.format(invoice.amount_excluding_vat || invoiceTotals.amount || 0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">MVA:</span>
                  <span className="font-semibold text-gray-900">
                    {formatter.format(invoice.vat || invoiceTotals.vat || 0)}
                  </span>
                </div>
                <div className="flex justify-between pt-2 border-t border-gray-300">
                  <span className="text-lg font-semibold text-gray-900">Totalt:</span>
                  <span className="text-lg font-bold text-gray-900">
                    {formatter.format(invoice.amount || invoiceTotals.amount_gross || 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Postings by Voucher */}
        <div className="space-y-6">
          <h3 className="text-xl font-semibold text-gray-900">
            Posteringer ({invoice.postings?.length || 0})
          </h3>

          {Object.keys(groupedPostings).length === 0 ? (
            <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 text-center">
              <p className="text-gray-600">Ingen posteringer funnet</p>
            </div>
          ) : (
            Object.entries(groupedPostings).map(([voucherId, postings]) => {
              const voucherTotals = calculatePostingTotals(postings);
              const voucherProjects = new Set();
              postings.forEach((p) => {
                if (p.project_id) voucherProjects.add(p.project_id);
              });

              return (
                <div
                  key={voucherId}
                  className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden"
                >
                  <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">
                          Voucher {voucherId !== "no-voucher" ? voucherId : "Uten voucher"}
                        </h4>
                        <p className="text-sm text-gray-600 mt-1">
                          {postings.length} postering{postings.length !== 1 ? "er" : ""}
                          {voucherProjects.size > 0 && (
                            <span className="ml-2">
                              • {voucherProjects.size} prosjekt{voucherProjects.size !== 1 ? "er" : ""}
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-600">Totalt for voucher:</div>
                        <div className="text-lg font-bold text-gray-900">
                          {formatter.format(voucherTotals.amount_gross)}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Dato
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Beskrivelse
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Prosjekt
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Konto
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Beløp ekskl. mva
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              MVA
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Totalt
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {postings.map((posting, idx) => {
                            const postingVat = (parseFloat(posting.amount_gross || 0) - parseFloat(posting.amount || 0));
                            return (
                              <tr key={posting.id || idx} className="hover:bg-gray-50">
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                  {formattedDate(posting.date)}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  {posting.description || "Ingen beskrivelse"}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  {posting.project ? (
                                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                                      {posting.project.name}
                                    </span>
                                  ) : (
                                    <span className="text-sm text-gray-400">-</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                  {posting.account_number || posting.account_id || "-"}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">
                                  {formatter.format(posting.amount || 0)}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">
                                  {formatter.format(postingVat)}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                                  {formatter.format(posting.amount_gross || posting.amount || 0)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot className="bg-gray-50">
                          <tr>
                            <td colSpan="4" className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                              Sum for voucher:
                            </td>
                            <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                              {formatter.format(voucherTotals.amount)}
                            </td>
                            <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                              {formatter.format(voucherTotals.vat)}
                            </td>
                            <td className="px-4 py-3 text-right text-sm font-bold text-gray-900">
                              {formatter.format(voucherTotals.amount_gross)}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
        </div>
      </div>
    </div>
  );
};


